import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useConversationHistory } from '../hooks/useConversationHistory.ts'
import { useConversationalMode } from '../hooks/useConversationalMode.ts'
import { getMessageLimit, useMessageLimits } from '../hooks/useMessageLimits.ts'
import {
  analyzeWithLLM,
  analyzeWithLLMProjectAutofillAsync,
  fileTooLargeUserMessage,
  isPdfLikeAttachment,
  PROJECT_AUTOFILL_CHAT_MAX_ATTACHMENT_BYTES,
  type LLMAnalysisResult,
  type LLMServiceError,
  type ProjectAutofillStreamPipelineEvent
} from '../services/llmService.ts'
import {
  loadConversations,
  normalizeStoredConversationId,
  type ConversationSession,
  type Message
} from '../utils/conversationStorage.ts'
import { serverMessageRowToMessage, type ApiChatMessageRow } from '../services/chatConversationService.ts'
import { sanitizePublicAssistantText } from '../utils/conversationalMode/sanitizePublicAssistantText.ts'
import ChatSearchModal from '../components/Dashboard/DashboardHeader/ConversationalMode/ChatSearchModal/ChatSearchModal.tsx'
import { ACTION_ID_TO_AGENT, AGENT_NAMES, isProjectAutofillAgentId } from '../constants/agentNames.ts'
import { AGENT_COLLECTED_DATA } from '../constants/agentIntents.ts'
import {
  hasPendingImage,
  getPendingImage,
  setPendingImageFromBlob,
  getPendingImageAsFile,
  clearPendingImage,
  getPendingAttachmentMime,
  getImageStorageKey
} from '../utils/agentImageStorage.ts'
import { compressImage } from '../utils/imageCompression.ts'

export interface ConversationalAgentContextValue {
  currentSession: ReturnType<typeof useConversationHistory>['currentSession']
  allSessions: ReturnType<typeof useConversationHistory>['allSessions']
  createSession: ReturnType<typeof useConversationHistory>['createSession']
  loadSession: ReturnType<typeof useConversationHistory>['loadSession']
  addMessage: ReturnType<typeof useConversationHistory>['addMessage']
  removeSession: ReturnType<typeof useConversationHistory>['removeSession']
  clearCurrentSession: ReturnType<typeof useConversationHistory>['clearCurrentSession']
  isConversationalModeActive: boolean
  activateConversationalMode: ReturnType<typeof useConversationalMode>['activate']
  deactivateConversationalMode: ReturnType<typeof useConversationalMode>['deactivate']
  canSendMessage: boolean
  remainingMessages: number
  hasReachedLimit: boolean
  limit: number
  sendMessage: (message: string, options?: ConversationalSendMessageOptions) => Promise<LLMAnalysisResult | null>
  isProcessing: boolean
  userId: string | undefined
  userData: any
  activeAgent: string
  setActiveAgent: (agent: string) => void
  collectedData: Record<string, any> | null
  setCollectedData: (data: Record<string, any> | null) => void
  missingFields: string[]
  readyToExecute: boolean
  setMissingFields: (fields: string[]) => void
  setReadyToExecute: (v: boolean) => void
  hasPendingImage: boolean
  imageUploadAcknowledged: boolean
  setImageUploadAcknowledged: (v: boolean) => void
  storePendingImage: (file: File) => Promise<void>
  clearPendingImage: () => void
  pendingImagePreview: string | null
  pendingAttachmentMime: string | null
  pendingAttachmentFileName: string | null
  pendingAttachmentSizeBytes: number | null
  lastAnalyzedDocumentationFileName: string | null
  documentPipelineLoopActive: boolean
  documentPipelinePhase: string | null
  documentPipelineStreamHint: DocumentPipelineStreamHint
  documentPipelinePrimaryFileOriginalName: string | null
  documentPipelinePrimaryFileSizeBytes: number | null
  openChatHistoryModal: () => void
}

const ConversationalAgentContext = createContext<ConversationalAgentContextValue | null>(null)

export function useConversationalAgent(): ConversationalAgentContextValue {
  const value = useContext(ConversationalAgentContext)
  if (!value) {
    throw new Error('useConversationalAgent must be used within ConversationalAgentProvider')
  }
  return value
}

export function useConversationalAgentOptional(): ConversationalAgentContextValue | null {
  return useContext(ConversationalAgentContext)
}

export type DocumentPipelineStreamHint = 'processing' | 'still_working' | null

export type ConversationalSendMessageOptions = {
  confirmExecution?: boolean
  activeAgentOverride?: string
  targetSessionId?: string
}

function pickConversationIdFromLlm(result: LLMAnalysisResult): string | undefined {
  return normalizeStoredConversationId(result?.conversationId)
}

function enrichAssistantMessageFromLlmData(
  base: Message,
  llmResult: LLMAnalysisResult,
  ctx: {
    collectedData: Record<string, any> | null
    targetSessionStorageAnchor: string
    userId?: string
    doConfirm: boolean
    payloadWithImage: Record<string, any>
  }
): Message {
  const { collectedData, targetSessionStorageAnchor, userId, doConfirm, payloadWithImage } = ctx
  const isCreatedPortfolio =
    (llmResult as any).data &&
    typeof (llmResult as any).data.id !== 'undefined' &&
    Array.isArray((llmResult as any).data.portfolio_images)
  const isCreatedPost =
    (llmResult as any).data &&
    typeof (llmResult as any).data.id !== 'undefined' &&
    ((llmResult as any).data.content != null ||
      Array.isArray((llmResult as any).data.images) ||
      Array.isArray((llmResult as any).data.video))
  let out: Message = { ...base }
  if (
    (llmResult.actionId === 'guardar-portfolio-item' || llmResult.actionId === 'guardar-post') &&
    (llmResult as any).collectedData?.image_uploaded === true
  ) {
    const previewUrl = getPendingImage(targetSessionStorageAnchor, userId)
    if (previewUrl && out.llmRawResponse) {
      try {
        const o = JSON.parse(out.llmRawResponse)
        o.imagePreviewUrl = previewUrl
        out = { ...out, llmRawResponse: JSON.stringify(o) }
      } catch {
        //
      }
    }
  }
  if (isCreatedPortfolio) {
    out = {
      ...out,
      createdPortfolio: {
        ...(llmResult as any).data,
        type:
          (llmResult as any).data?.type ??
          (collectedData?.type as 'producto' | 'servicio' | undefined)
      }
    }
  }
  let createdPost = isCreatedPost ? (llmResult as any).data : undefined
  if (
    !createdPost &&
    (llmResult as any).delegate === 'general-agent' &&
    doConfirm &&
    payloadWithImage?.intent === 'create_post'
  ) {
    createdPost = {
      id: 0,
      content: payloadWithImage?.content ?? undefined
    }
  }
  if (createdPost) {
    out = { ...out, createdPost }
  }
  if (llmResult.needsData && llmResult.data) {
    out = {
      ...out,
      data: llmResult.data,
      dataTotal: llmResult.total || 0,
      needsData: true,
      quotationType: (llmResult as any).quotationType,
      workVisibility: (llmResult as any).workVisibility,
      isPublic: (llmResult as any).isPublic,
      noResultsMessage: llmResult.noResultsMessage
    }
  }
  const sanitizedContent = sanitizePublicAssistantText(out.content)
  let next: Message = { ...out, content: sanitizedContent }
  if (next.llmRawResponse) {
    try {
      const o = JSON.parse(next.llmRawResponse) as Record<string, unknown>
      if (typeof o.response === 'string') {
        o.response = sanitizedContent
        next = { ...next, llmRawResponse: JSON.stringify(o) }
      }
    } catch {
    }
  }
  return next
}

function pickHandoffUserIntentFromLlm(result: LLMAnalysisResult): string | undefined {
  const u =
    (result as any).data?.handoffContext?.userIntent ?? (result as any).handoffContext?.userIntent
  if (typeof u === 'string' && u.trim().length > 0) return u.trim()
  return undefined
}

interface ConversationalAgentProviderProps {
  children: React.ReactNode
}

export function ConversationalAgentProvider({ children }: ConversationalAgentProviderProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const userData = useSelector((state: any) => state.general?.userData)
  const isUserPremium = useSelector((state: any) => state.general?.isUserPremium ?? false)
  const userId = userData?.id

  const {
    currentSession,
    allSessions,
    createSession,
    loadSession,
    addMessage,
    removeSession,
    clearCurrentSession,
    syncSessionWithServer,
    applyCanonicalTurn
  } = useConversationHistory(userId)

  const {
    isActive: isConversationalModeActive,
    activate: activateConversationalMode,
    deactivate: deactivateConversationalMode
  } = useConversationalMode()

  const { canSendMessage, remainingMessages, hasReachedLimit, limit } = useMessageLimits(
    currentSession,
    isUserPremium
  )

  const [isProcessing, setIsProcessing] = useState(false)
  const [activeAgent, setActiveAgent] = useState<string>(AGENT_NAMES.general)
  const [collectedData, setCollectedData] = useState<Record<string, any> | null>(null)
  const [missingFields, setMissingFields] = useState<string[]>([])
  const [readyToExecute, setReadyToExecute] = useState(false)
  const [imageUploadAcknowledged, setImageUploadAcknowledged] = useState(false)
  const [, forceImagePreviewUpdate] = useState(0)
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null)
  const [pendingImageObjectUrl, setPendingImageObjectUrl] = useState<string | null>(null)
  const [documentPipelineLoopActive, setDocumentPipelineLoopActive] = useState(false)
  const [documentPipelinePhase, setDocumentPipelinePhase] = useState<string | null>(null)
  const [documentPipelineStreamHint, setDocumentPipelineStreamHint] =
    useState<DocumentPipelineStreamHint>(null)
  const [documentPipelinePrimaryFileOriginalName, setDocumentPipelinePrimaryFileOriginalName] =
    useState<string | null>(null)
  const [documentPipelinePrimaryFileSizeBytes, setDocumentPipelinePrimaryFileSizeBytes] =
    useState<number | null>(null)
  const [lastAnalyzedDocumentationFileName, setLastAnalyzedDocumentationFileName] = useState<
    string | null
  >(null)
  const [projectAutofillHandoffUserIntent, setProjectAutofillHandoffUserIntent] = useState<
    string | null
  >(null)
  const [chatHistoryModalOpen, setChatHistoryModalOpen] = useState(false)

  const openChatHistoryModal = useCallback(() => {
    setChatHistoryModalOpen(true)
  }, [])

  const closeChatHistoryModal = useCallback(() => {
    setChatHistoryModalOpen(false)
  }, [])

  const handleGlobalChatSelectSession = useCallback(
    (sessionId: string) => {
      setChatHistoryModalOpen(false)
      void loadSession(sessionId).then((session) => {
        if (session) {
          activateConversationalMode()
          if (location.pathname !== '/dashboard') {
            navigate('/dashboard', { state: { fromConversation: true } })
          }
        }
      })
    },
    [loadSession, activateConversationalMode, location.pathname, navigate]
  )

  const handleGlobalChatCreateNewSession = useCallback(() => {
    setChatHistoryModalOpen(false)
    createSession()
    activateConversationalMode()
    if (location.pathname !== '/dashboard') {
      navigate('/dashboard', { state: { fromConversation: true } })
    }
  }, [createSession, activateConversationalMode, location.pathname, navigate])

  const sessionId = currentSession?.sessionId
  const storageKeyAnchor =
    currentSession?.conversationId != null &&
    typeof currentSession.conversationId === 'string' &&
    /^\d+$/.test(currentSession.conversationId.trim())
      ? currentSession.conversationId.trim()
      : sessionId ?? ''

  const draftStorageKey =
    userId && storageKeyAnchor.length > 0
      ? `project-autofill-draft-${userId}-${storageKeyAnchor}`
      : null
  const agentStorageKey =
    userId && storageKeyAnchor.length > 0
      ? `${AGENT_NAMES.projectAutofillAgent}-${userId}-${storageKeyAnchor}`
      : null

  useEffect(() => {
    if (!userId || !sessionId) return
    const cid = normalizeStoredConversationId(currentSession?.conversationId)
    if (!cid) return
    const oldDraft = `project-autofill-draft-${userId}-${sessionId}`
    const newDraft = `project-autofill-draft-${userId}-${cid}`
    const d = localStorage.getItem(oldDraft)
    if (d && !localStorage.getItem(newDraft)) {
      localStorage.setItem(newDraft, d)
      localStorage.removeItem(oldDraft)
    }
    const oldAgent = `${AGENT_NAMES.projectAutofillAgent}-${userId}-${sessionId}`
    const newAgent = `${AGENT_NAMES.projectAutofillAgent}-${userId}-${cid}`
    const a = localStorage.getItem(oldAgent)
    if (a && !localStorage.getItem(newAgent)) {
      localStorage.setItem(newAgent, a)
      localStorage.removeItem(oldAgent)
    }
    const oldImg = getImageStorageKey(sessionId, userId)
    const newImg = getImageStorageKey(cid, userId)
    const img = localStorage.getItem(oldImg)
    if (img && !localStorage.getItem(newImg)) {
      localStorage.setItem(newImg, img)
      localStorage.removeItem(oldImg)
    }
  }, [userId, sessionId, currentSession?.conversationId])

  useEffect(() => {
    setPendingImageFile(null)
    setPendingImageObjectUrl(null)
    setReadyToExecute(false)
    setCollectedData(null)
    setMissingFields([])
    setActiveAgent(AGENT_NAMES.general)
    setDocumentPipelineLoopActive(false)
    setDocumentPipelinePhase(null)
    setDocumentPipelineStreamHint(null)
    setDocumentPipelinePrimaryFileOriginalName(null)
    setDocumentPipelinePrimaryFileSizeBytes(null)
    setLastAnalyzedDocumentationFileName(null)
    setProjectAutofillHandoffUserIntent(null)
  }, [sessionId])

  useEffect(() => {
    const pathname = location.pathname
    const fromConversation = (location.state as any)?.fromConversation === true
    const sessionIdFromUrl = new URLSearchParams(location.search).get('session')
    if (pathname !== '/dashboard' && !fromConversation && !sessionIdFromUrl) {
      clearCurrentSession()
    }
  }, [location.pathname, (location.state as any)?.fromConversation, location.search])

  useEffect(() => {
    setImageUploadAcknowledged(false)
  }, [sessionId])

  useEffect(() => {
    if (!pendingImageFile) {
      setPendingImageObjectUrl(null)
      return
    }
    const objectUrl = URL.createObjectURL(pendingImageFile)
    setPendingImageObjectUrl(objectUrl)
    return () => {
      URL.revokeObjectURL(objectUrl)
    }
  }, [pendingImageFile])

  useEffect(() => {
    if (!draftStorageKey) return
    if (activeAgent !== AGENT_NAMES.projectAutofillAgent) return
    const storedDraftId = localStorage.getItem(draftStorageKey)
    if (!storedDraftId) return
    setCollectedData((prev) => ({ ...(prev || {}), draftId: storedDraftId }))
  }, [activeAgent, draftStorageKey])

  useEffect(() => {
    if (!agentStorageKey || !userId || !sessionId) return
    let storedActiveAgent = localStorage.getItem(agentStorageKey)
    if (!storedActiveAgent) {
      const legacyKey = `project-autofill-agent-${userId}-${sessionId}`
      const legacy = localStorage.getItem(legacyKey)
      if (legacy && isProjectAutofillAgentId(legacy)) {
        localStorage.setItem(agentStorageKey, AGENT_NAMES.projectAutofillAgent)
        localStorage.removeItem(legacyKey)
        storedActiveAgent = AGENT_NAMES.projectAutofillAgent
      }
    }
    if (isProjectAutofillAgentId(storedActiveAgent)) {
      setActiveAgent(AGENT_NAMES.projectAutofillAgent)
    }
  }, [agentStorageKey, userId, sessionId])

  useEffect(() => {
    if (!agentStorageKey) return
    if (activeAgent === AGENT_NAMES.projectAutofillAgent) {
      localStorage.setItem(agentStorageKey, AGENT_NAMES.projectAutofillAgent)
    } else {
      localStorage.removeItem(agentStorageKey)
    }
  }, [activeAgent, agentStorageKey])

  const storePendingImage = useCallback(
    async (file: File) => {
      const isImage = file.type.startsWith('image/')
      const isVideo = file.type.startsWith('video/')
      const isPdf = file.type === 'application/pdf'
      const canUsePdf = activeAgent === AGENT_NAMES.projectAutofillAgent || collectedData?.intent === 'project_autofill'
      if (!isImage && !isVideo && !(isPdf && canUsePdf)) return
      if (isVideo && collectedData?.intent !== 'create_post') return
      setImageUploadAcknowledged(false)
      setPendingImageFile(file)
      const blob = isImage
        ? await compressImage(file)
        : file
      await setPendingImageFromBlob(blob, storageKeyAnchor || sessionId || undefined, userId)
      forceImagePreviewUpdate((n) => n + 1)
    },
    [sessionId, userId, collectedData?.intent, activeAgent, storageKeyAnchor]
  )

  const pendingImage =
    hasPendingImage(storageKeyAnchor || sessionId, userId) || !!pendingImageFile

  useEffect(() => {
    const n = documentPipelinePrimaryFileOriginalName?.trim()
    if (n) setLastAnalyzedDocumentationFileName(n)
  }, [documentPipelinePrimaryFileOriginalName])

  const pendingAttachmentFileName = useMemo(() => {
    const fromMem = pendingImageFile?.name?.trim()
    if (fromMem) return fromMem
    const f = getPendingImageAsFile(storageKeyAnchor || sessionId, userId)
    return f?.name?.trim() || null
  }, [pendingImageFile, storageKeyAnchor, sessionId, userId])

  const pendingAttachmentSizeBytes =
    typeof pendingImageFile?.size === 'number' && Number.isFinite(pendingImageFile.size)
      ? pendingImageFile.size
      : null

  const clearPendingImageFn = useCallback(() => {
    setPendingImageFile(null)
    clearPendingImage(storageKeyAnchor || sessionId, userId)
    setImageUploadAcknowledged(false)
    forceImagePreviewUpdate((n) => n + 1)
  }, [sessionId, userId, storageKeyAnchor])

  const sendMessage = useCallback(
    async (message: string, options?: ConversationalSendMessageOptions): Promise<LLMAnalysisResult | null> => {
      const explicitSessionId = options?.targetSessionId
      const conversationsSnapshot = loadConversations(userId)
      const sessionForLimit = explicitSessionId
        ? conversationsSnapshot.sessions.find((s) => s.sessionId === explicitSessionId) ?? null
        : conversationsSnapshot.currentSessionId
          ? conversationsSnapshot.sessions.find((s) => s.sessionId === conversationsSnapshot.currentSessionId) ??
            null
          : null
      const messageLimit = getMessageLimit(isUserPremium)
      const underLimit =
        !sessionForLimit || (sessionForLimit.messageCount ?? 0) < messageLimit
      if (!underLimit && !options?.confirmExecution) {
        return null
      }

      const conversations = loadConversations(userId)
      let session: ConversationSession | null = null
      if (explicitSessionId) {
        session = conversations.sessions.find((s) => s.sessionId === explicitSessionId) ?? null
      } else if (conversations.currentSessionId) {
        session = conversations.sessions.find((s) => s.sessionId === conversations.currentSessionId) || null
      }
      if (!session) {
        session = createSession()
      }

      const targetSessionId = session.sessionId

      const msgContent = options?.confirmExecution ? '' : message.trim()
      if (!msgContent && !options?.confirmExecution) {
        return null
      }

      const userMessage: Message = {
        id: `msg-${Date.now()}-user`,
        role: 'user',
        content: options?.confirmExecution ? 'Confirmar creación' : message,
        timestamp: new Date().toISOString()
      }

      addMessage(userMessage, targetSessionId)
      setIsProcessing(true)

      await new Promise((resolve) => setTimeout(resolve, 100))

      const agentForRequest = options?.activeAgentOverride ?? activeAgent

      try {
        const sessionRow = loadConversations(userId).sessions.find(
          (s) => s.sessionId === targetSessionId
        )
        const pendingAnchor =
          normalizeStoredConversationId(sessionRow?.conversationId) ?? targetSessionId
        const existingConversationId = normalizeStoredConversationId(sessionRow?.conversationId)
        const conversationOrClient =
          existingConversationId != null
            ? { conversationId: existingConversationId }
            : { clientSessionId: targetSessionId }

        const currentSessionForLLM = sessionRow || session

        const allMessages = currentSessionForLLM.messages || []
        const messagesWithoutCurrent =
          allMessages.length > 0 && allMessages[allMessages.length - 1].id === userMessage.id
            ? allMessages.slice(0, -1)
            : allMessages
        const previousMessages = messagesWithoutCurrent.slice(-14)
        const historyForLLM = previousMessages.map((m) => {
          let content = m.role === 'assistant' && m.llmRawResponse ? m.llmRawResponse : m.content
          if (m.role === 'assistant' && m.llmRawResponse) {
            try {
              const parsed = JSON.parse(m.llmRawResponse)
              if ('imagePreviewUrl' in parsed) {
                const { imagePreviewUrl: _, ...rest } = parsed
                content = JSON.stringify(rest)
              }
            } catch {
            }
          }
          return { role: m.role, content }
        })

        const payloadCollectedData =
          collectedData ?? AGENT_COLLECTED_DATA[agentForRequest]
        const hasStoredImage = hasPendingImage(pendingAnchor, userId)
        const hasImageInMemory = !!pendingImageFile
        const imageUploaded = hasStoredImage || hasImageInMemory
        const doConfirm = options?.confirmExecution === true
        const payloadWithImage = { ...payloadCollectedData }
        if (imageUploaded && !doConfirm) {
          payloadWithImage.image_uploaded = true
          setImageUploadAcknowledged(true)
        }
        const isProjectAutofillFlow =
          agentForRequest === AGENT_NAMES.projectAutofillAgent ||
          (payloadCollectedData as { intent?: string } | undefined)?.intent === 'project_autofill'
        let imageToSend: File | undefined
        if (doConfirm && imageUploaded) {
          imageToSend =
            pendingImageFile ?? getPendingImageAsFile(pendingAnchor, userId) ?? undefined
        } else if (!doConfirm && imageUploaded && isProjectAutofillFlow) {
          imageToSend =
            pendingImageFile ?? getPendingImageAsFile(pendingAnchor, userId) ?? undefined
        }

        const effectiveAgentForPayload =
          imageToSend && isPdfLikeAttachment(imageToSend) && isProjectAutofillFlow
            ? AGENT_NAMES.projectAutofillAgent
            : agentForRequest

        const handoffForRequest =
          effectiveAgentForPayload === AGENT_NAMES.projectAutofillAgent && projectAutofillHandoffUserIntent
            ? { handoffContext: { userIntent: projectAutofillHandoffUserIntent } }
            : {}

        const analyzeOptionsBase = {
          activeAgent: effectiveAgentForPayload,
          collectedData: payloadWithImage,
          imageUploaded,
          readyToExecute: doConfirm,
          image: imageToSend,
          ...handoffForRequest,
          ...conversationOrClient
        }

        const runAnalyze = (draftIdOverride?: string) =>
          analyzeWithLLM(msgContent || (doConfirm ? 'Confirmar' : message), historyForLLM, {
            ...analyzeOptionsBase,
            draftId: draftIdOverride !== undefined ? draftIdOverride : payloadWithImage?.draftId
          })

        const useProjectAutofillPdfStream =
          Boolean(imageToSend && isPdfLikeAttachment(imageToSend) && isProjectAutofillFlow)

        const runProjectAutofillRequest = async (draftIdOverride?: string) => {
          if (useProjectAutofillPdfStream) {
            return analyzeWithLLMProjectAutofillAsync(
              msgContent || (doConfirm ? 'Confirmar' : message),
              historyForLLM,
              {
                ...analyzeOptionsBase,
                draftId: draftIdOverride !== undefined ? draftIdOverride : payloadWithImage?.draftId
              },
              (event: ProjectAutofillStreamPipelineEvent) => {
                if (event.kind === 'progress') {
                  setDocumentPipelineStreamHint(null)
                  setDocumentPipelinePhase(event.phase)
                  if (Object.prototype.hasOwnProperty.call(event, 'primaryFileOriginalName')) {
                    setDocumentPipelinePrimaryFileOriginalName(
                      event.primaryFileOriginalName ?? null
                    )
                    setDocumentPipelinePrimaryFileSizeBytes(
                      Object.prototype.hasOwnProperty.call(event, 'primaryFileSizeBytes')
                        ? event.primaryFileSizeBytes ?? null
                        : null
                    )
                  }
                } else if (event.phase) {
                  setDocumentPipelinePhase(event.phase)
                  setDocumentPipelineStreamHint('still_working')
                } else {
                  setDocumentPipelinePhase(null)
                  setDocumentPipelineStreamHint('processing')
                }
              },
              {
                onAsyncAccepted: async ({ conversationId, draftId }) => {
                  setCollectedData((prev) => {
                    const next = { ...(prev || {}), draftId }
                    delete (next as { nextAction?: string }).nextAction
                    return next
                  })
                  if (userId && /^\d+$/.test(conversationId)) {
                    await syncSessionWithServer(targetSessionId, conversationId)
                    localStorage.setItem(
                      `project-autofill-draft-${userId}-${conversationId}`,
                      draftId
                    )
                    localStorage.setItem(
                      `${AGENT_NAMES.projectAutofillAgent}-${userId}-${conversationId}`,
                      AGENT_NAMES.projectAutofillAgent
                    )
                  }
                }
              }
            )
          }
          return runAnalyze(draftIdOverride)
        }

        if (
          imageToSend &&
          isProjectAutofillFlow &&
          imageToSend.size > PROJECT_AUTOFILL_CHAT_MAX_ATTACHMENT_BYTES
        ) {
          addMessage(
            {
              id: `msg-${Date.now()}-error`,
              role: 'assistant',
              content: fileTooLargeUserMessage(PROJECT_AUTOFILL_CHAT_MAX_ATTACHMENT_BYTES),
              timestamp: new Date().toISOString()
            },
            targetSessionId
          )
          return null
        }

        if (useProjectAutofillPdfStream) {
          setDocumentPipelineLoopActive(true)
          setDocumentPipelinePhase(null)
          setDocumentPipelineStreamHint(null)
          setDocumentPipelinePrimaryFileOriginalName(null)
          setDocumentPipelinePrimaryFileSizeBytes(null)
        }

        let llmResult: LLMAnalysisResult
        try {
          llmResult = await runProjectAutofillRequest()
        } catch (err) {
          const serviceErr = err as LLMServiceError
          const isDraftNotFound =
            serviceErr?.code === 'DRAFT_NOT_FOUND' &&
            (agentForRequest === AGENT_NAMES.projectAutofillAgent ||
              effectiveAgentForPayload === AGENT_NAMES.projectAutofillAgent)
          if (!isDraftNotFound) throw err
          if (draftStorageKey) localStorage.removeItem(draftStorageKey)
          setCollectedData((prev) => {
            const next = { ...(prev || {}) }
            delete (next as any).draftId
            return next
          })
          llmResult = await runProjectAutofillRequest('')
        }

        const anchorForPersistedKeys =
          pickConversationIdFromLlm(llmResult) ?? pendingAnchor
        const persistedDraftKey =
          userId && anchorForPersistedKeys
            ? `project-autofill-draft-${userId}-${anchorForPersistedKeys}`
            : null
        const persistedAgentKey =
          userId && anchorForPersistedKeys
            ? `${AGENT_NAMES.projectAutofillAgent}-${userId}-${anchorForPersistedKeys}`
            : null

        if (doConfirm && imageUploaded) {
          setPendingImageFile(null)
          clearPendingImage(pendingAnchor, userId)
          setImageUploadAcknowledged(false)
        }
        if (!doConfirm && imageUploaded && isProjectAutofillFlow) {
          setPendingImageFile(null)
          clearPendingImage(pendingAnchor, userId)
          setImageUploadAcknowledged(false)
        }
        if ((llmResult as any).collectedData || (llmResult as any).nextAction) {
          setCollectedData((prev) => ({
            ...(prev || {}),
            ...((llmResult as any).collectedData &&
            typeof (llmResult as any).collectedData === 'object'
              ? (llmResult as any).collectedData
              : {}),
            ...((llmResult as any).nextAction
              ? { nextAction: String((llmResult as any).nextAction) }
              : {})
          }))
        }
        if ((llmResult as any).draftId) {
          setCollectedData((prev) => {
            const next = { ...(prev || {}), draftId: String((llmResult as any).draftId) }
            delete (next as { nextAction?: string }).nextAction
            return next
          })
          if (persistedDraftKey) {
            localStorage.setItem(persistedDraftKey, String((llmResult as any).draftId))
          }
        }
        if (Array.isArray((llmResult as any).missingFields)) {
          setMissingFields((llmResult as any).missingFields)
        }
        const isCreatedPortfolio = (llmResult as any).data &&
          typeof (llmResult as any).data.id !== 'undefined' &&
          Array.isArray((llmResult as any).data.portfolio_images)
        const isCreatedPost = (llmResult as any).data &&
          typeof (llmResult as any).data.id !== 'undefined' &&
          ((llmResult as any).data.content != null ||
            Array.isArray((llmResult as any).data.images) ||
            Array.isArray((llmResult as any).data.video))
        if ((llmResult as any).readyToExecute !== undefined) {
          setReadyToExecute((llmResult as any).readyToExecute === true)
        } else if ((llmResult as any).delegate === 'general-agent' || isCreatedPortfolio || isCreatedPost) {
          setReadyToExecute(false)
        } else if (doConfirm && llmResult.response) {
          const successKeywords = ['listo', 'publicado', 'creado', 'listo!']
          if (successKeywords.some(kw => llmResult.response.toLowerCase().includes(kw))) {
            setReadyToExecute(false)
          }
        }
        if (llmResult.actionId && ACTION_ID_TO_AGENT[llmResult.actionId]) {
          const nextAgent = ACTION_ID_TO_AGENT[llmResult.actionId]
          if (nextAgent !== AGENT_NAMES.projectAutofillAgent) {
            setProjectAutofillHandoffUserIntent(null)
          }
          setActiveAgent(nextAgent)
          if (persistedAgentKey && nextAgent === AGENT_NAMES.projectAutofillAgent) {
            localStorage.setItem(persistedAgentKey, AGENT_NAMES.projectAutofillAgent)
          }
          if (!(llmResult as any).collectedData && !(llmResult as any).nextAction) {
            const defaultCollected = AGENT_COLLECTED_DATA[nextAgent] || null
            setCollectedData(defaultCollected)
          }
        }
        if ((llmResult as any).activeAgent === AGENT_NAMES.projectAutofillAgent) {
          setActiveAgent(AGENT_NAMES.projectAutofillAgent)
          if (persistedAgentKey) {
            localStorage.setItem(persistedAgentKey, AGENT_NAMES.projectAutofillAgent)
          }
          if ((llmResult as any).draftId && persistedDraftKey) {
            localStorage.setItem(persistedDraftKey, String((llmResult as any).draftId))
          }
        }
        if ((llmResult as any).delegate === 'general-agent') {
          setProjectAutofillHandoffUserIntent(null)
          setActiveAgent(AGENT_NAMES.general)
          setCollectedData(null)
          setMissingFields([])
          setReadyToExecute(false)
          setPendingImageFile(null)
          clearPendingImage(pendingAnchor, userId)
          setImageUploadAcknowledged(false)
          if (persistedDraftKey) {
            localStorage.removeItem(persistedDraftKey)
          }
          if (persistedAgentKey) {
            localStorage.removeItem(persistedAgentKey)
          }
          if (draftStorageKey && draftStorageKey !== persistedDraftKey) {
            localStorage.removeItem(draftStorageKey)
          }
          if (agentStorageKey && agentStorageKey !== persistedAgentKey) {
            localStorage.removeItem(agentStorageKey)
          }
        }
        if (isCreatedPortfolio) {
          setCollectedData(null)
          setMissingFields([])
        }
        if (isCreatedPost) {
          setCollectedData(null)
          setMissingFields([])
          setPendingImageFile(null)
          clearPendingImage(pendingAnchor, userId)
          setImageUploadAcknowledged(false)
        }

        if ((llmResult as any).delegate !== 'general-agent') {
          const incomingHandoff = pickHandoffUserIntentFromLlm(llmResult)
          if (incomingHandoff) setProjectAutofillHandoffUserIntent(incomingHandoff)
        }

        const um = llmResult.userMessage
        const am = llmResult.assistantMessage
        let appliedCanonical = false
        if (um && am && um.role === 'user' && am.role === 'assistant') {
          const uParsed = serverMessageRowToMessage(um as ApiChatMessageRow)
          const aParsed = serverMessageRowToMessage(am as ApiChatMessageRow)
          if (uParsed && aParsed) {
            const aEnriched = enrichAssistantMessageFromLlmData(aParsed, llmResult, {
              collectedData,
              targetSessionStorageAnchor: pendingAnchor,
              userId,
              doConfirm,
              payloadWithImage
            })
            applyCanonicalTurn(targetSessionId, uParsed, aEnriched)
            appliedCanonical = true
          }
        }

        if (!appliedCanonical) {
          const publicResponse = sanitizePublicAssistantText(llmResult.response)
          const llmRawResponseObj: any = {
            actionId: llmResult.actionId,
            activeAgent: (llmResult as any).activeAgent,
            confidence: llmResult.confidence,
            response: publicResponse,
            guideMode: llmResult.guideMode || false
          }
          if (Array.isArray(llmResult.suggestedActions) && llmResult.suggestedActions.length > 0) {
            llmRawResponseObj.suggestedActions = llmResult.suggestedActions
          }
          if ((llmResult as any).draftId) {
            llmRawResponseObj.draftId = (llmResult as any).draftId
          }
          if ((llmResult as any).nextAction) {
            llmRawResponseObj.nextAction = (llmResult as any).nextAction
          }
          if ((llmResult as any).validation) {
            const v = (llmResult as any).validation as Record<string, unknown>
            const w = v.warnings
            const bi = v.blockingIssues
            const mapStrArr = (arr: unknown) =>
              Array.isArray(arr)
                ? arr.map((x) => (typeof x === 'string' ? sanitizePublicAssistantText(x) : x))
                : arr
            llmRawResponseObj.validation = {
              ...v,
              ...(Array.isArray(w) ? { warnings: mapStrArr(w) } : {}),
              ...(Array.isArray(bi) ? { blockingIssues: mapStrArr(bi) } : {})
            }
          }
          if ((llmResult as any).draft) {
            llmRawResponseObj.draft = (llmResult as any).draft
          }

          if ((llmResult as any).missingFields) {
            llmRawResponseObj.missingFields = (llmResult as any).missingFields
          }
          if ((llmResult as any).collectedData) {
            llmRawResponseObj.collectedData = (llmResult as any).collectedData
          }
          if ((llmResult as any).readyToExecute !== undefined) {
            llmRawResponseObj.readyToExecute = (llmResult as any).readyToExecute
          }
          if (
            (llmResult.actionId === 'guardar-portfolio-item' || llmResult.actionId === 'guardar-post') &&
            (llmResult as any).collectedData?.image_uploaded === true
          ) {
            const previewUrl = getPendingImage(pendingAnchor, userId)
            if (previewUrl) llmRawResponseObj.imagePreviewUrl = previewUrl
          }

          if (llmResult.needsData && llmResult.data) {
            llmRawResponseObj.needsData = true
            llmRawResponseObj.data = llmResult.data
            llmRawResponseObj.total = llmResult.total || 0
            if ((llmResult as any).quotationType)
              llmRawResponseObj.quotationType = (llmResult as any).quotationType
            if ((llmResult as any).workVisibility)
              llmRawResponseObj.workVisibility = (llmResult as any).workVisibility
            if ((llmResult as any).isPublic !== undefined)
              llmRawResponseObj.isPublic = (llmResult as any).isPublic
            if (llmResult.noResultsMessage)
              llmRawResponseObj.noResultsMessage = llmResult.noResultsMessage
            if ((llmResult as any).searchTerm)
              llmRawResponseObj.searchTerm = (llmResult as any).searchTerm
            if ((llmResult as any).projectType)
              llmRawResponseObj.projectType = (llmResult as any).projectType
          }

          const createdPortfolio = isCreatedPortfolio
            ? {
                ...(llmResult as any).data,
                type:
                  (llmResult as any).data?.type ??
                  (collectedData?.type as 'producto' | 'servicio' | undefined)
              }
            : undefined

          let createdPost = isCreatedPost ? (llmResult as any).data : undefined
          if (!createdPost && (llmResult as any).delegate === 'general-agent' && doConfirm && payloadWithImage?.intent === 'create_post') {
            createdPost = {
              id: 0,
              content: payloadWithImage?.content ?? undefined
            }
          }

          const llmRawResponse = JSON.stringify(llmRawResponseObj)

          const assistantMessage: Message = {
            id: `msg-${Date.now()}-assistant`,
            role: 'assistant',
            content: publicResponse,
            timestamp: new Date().toISOString(),
            actionId: llmResult.actionId || null,
            guideMode: llmResult.guideMode || false,
            llmRawResponse,
            ...(createdPortfolio && { createdPortfolio }),
            ...(createdPost && { createdPost }),
            ...(llmResult.needsData &&
              llmResult.data && {
                data: llmResult.data,
                dataTotal: llmResult.total || 0,
                needsData: true,
                quotationType: (llmResult as any).quotationType,
                workVisibility: (llmResult as any).workVisibility,
                isPublic: (llmResult as any).isPublic,
                noResultsMessage: llmResult.noResultsMessage
              })
          }

          addMessage(assistantMessage, targetSessionId)
        }

        setIsProcessing(false)

        const cid =
          pickConversationIdFromLlm(llmResult) ??
          normalizeStoredConversationId(
            loadConversations(userId).sessions.find((s) => s.sessionId === targetSessionId)
              ?.conversationId
          )
        if (userId && cid != null && /^\d+$/.test(cid)) {
          await syncSessionWithServer(targetSessionId, cid)
        }
        return llmResult
      } catch (error) {
        const serviceErr = error as LLMServiceError
        let content =
          'Disculpá, hubo un problema procesando tu consulta. ¿Podrías intentar de nuevo?'
        if (
          serviceErr?.code === 'FILE_TOO_LARGE' &&
          (agentForRequest === AGENT_NAMES.projectAutofillAgent ||
            collectedData?.intent === 'project_autofill')
        ) {
          content =
            serviceErr.userMessage ||
            fileTooLargeUserMessage(serviceErr.maxBytes)
        } else if (serviceErr?.code === 'STREAM_TIMEOUT') {
          content =
            serviceErr.userMessage ||
            'El análisis del documento tardó demasiado. Intentá de nuevo en unos minutos.'
        } else if (serviceErr?.code === 'DOCUMENTATION_PIPELINE_FAILED') {
          content =
            serviceErr.userMessage ||
            'El análisis del documento falló. Intentá de nuevo.'
        } else if (
          (agentForRequest === AGENT_NAMES.projectAutofillAgent ||
            collectedData?.intent === 'project_autofill') &&
          typeof serviceErr?.message === 'string' &&
          serviceErr.message.trim().length > 0 &&
          (serviceErr?.code === 'UNSUPPORTED' ||
            serviceErr?.code === 'CHATBOT_STREAM_ERROR')
        ) {
          content = serviceErr.message.trim()
        }
        const pipelineInsight =
          serviceErr?.code === 'DOCUMENTATION_PIPELINE_FAILED'
            ? serviceErr.documentInsight ?? undefined
            : undefined
        const errorMsg: Message = {
          id: `msg-${Date.now()}-error`,
          role: 'assistant',
          content,
          timestamp: new Date().toISOString(),
          ...(pipelineInsight ? { documentInsight: pipelineInsight } : {})
        }
        addMessage(errorMsg, targetSessionId)
        return null
      } finally {
        setDocumentPipelineLoopActive(false)
        setDocumentPipelinePhase(null)
        setDocumentPipelineStreamHint(null)
        setDocumentPipelinePrimaryFileOriginalName(null)
        setDocumentPipelinePrimaryFileSizeBytes(null)
        setIsProcessing(false)
      }
    },
    [
      isUserPremium,
      createSession,
      addMessage,
      setCollectedData,
      userId,
      collectedData,
      activeAgent,
      pendingImageFile,
      draftStorageKey,
      agentStorageKey,
      projectAutofillHandoffUserIntent,
      syncSessionWithServer,
      applyCanonicalTurn
    ]
  )

  const value: ConversationalAgentContextValue = {
    currentSession,
    allSessions,
    createSession,
    loadSession,
    addMessage,
    removeSession,
    clearCurrentSession,
    isConversationalModeActive,
    activateConversationalMode,
    deactivateConversationalMode,
    canSendMessage,
    remainingMessages,
    hasReachedLimit,
    limit,
    sendMessage,
    isProcessing,
    userId,
    userData,
    activeAgent,
    setActiveAgent,
    collectedData,
    setCollectedData,
    missingFields,
    readyToExecute,
    setMissingFields,
    setReadyToExecute,
    hasPendingImage: pendingImage,
    imageUploadAcknowledged,
    setImageUploadAcknowledged,
    storePendingImage,
    clearPendingImage: clearPendingImageFn,
    pendingImagePreview:
      getPendingImage(storageKeyAnchor || sessionId, userId) ?? pendingImageObjectUrl,
    pendingAttachmentMime: getPendingAttachmentMime(
      pendingImageFile,
      storageKeyAnchor || sessionId,
      userId
    ),
    pendingAttachmentFileName,
    pendingAttachmentSizeBytes,
    lastAnalyzedDocumentationFileName,
    documentPipelineLoopActive,
    documentPipelinePhase,
    documentPipelineStreamHint,
    documentPipelinePrimaryFileOriginalName,
    documentPipelinePrimaryFileSizeBytes,
    openChatHistoryModal
  }

  return (
    <ConversationalAgentContext.Provider value={value}>
      {children}
      <ChatSearchModal
        isOpen={chatHistoryModalOpen}
        onClose={closeChatHistoryModal}
        sessions={allSessions}
        currentSessionId={currentSession?.sessionId ?? null}
        onSelectSession={handleGlobalChatSelectSession}
        onCreateNewSession={handleGlobalChatCreateNewSession}
        onDeleteSession={removeSession}
        userId={userId}
        userData={userData}
      />
    </ConversationalAgentContext.Provider>
  )
}
